import { atom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { safeJSONStorage } from "utils/safeStorage";
import type { TakeoverFlags } from "./takeover-flags";

// Statically imported by the nav and the signed-up guard, so it must not pull in the chat's own dependencies.

/** Locations whose manager chose Skip to Home: the takeover is never offered there again on this browser. */
export const skippedTakeoverAtom = atomWithStorage<Array<number>>(
  "qwick-takeover-skipped",
  [],
  safeJSONStorage<Array<number>>(),
  {
    getOnInit: true,
  },
);

export const skipTakeoverAtom = atom(null, (get, set, locationId: number) => {
  const skipped = get(skippedTakeoverAtom);
  if (!skipped.includes(locationId)) set(skippedTakeoverAtom, [...skipped, locationId]);
});

/** The Ask Qwick button's first-visit tooltip was closed or the button was used; it never shows again. */
export const askQwickTooltipSeenAtom = atomWithStorage(
  "qwick-ask-qwick-tooltip-seen",
  false,
  safeJSONStorage<boolean>(),
  {
    getOnInit: true,
  },
);

/** Where the signed-up guard sends a location still waiting on its first post; null sends it nowhere. */
export const firstPostDestination = (onboarding: { enabled: boolean; offered: boolean }) => {
  if (!onboarding.enabled) return "post-form";
  return onboarding.offered ? "takeover" : null;
};

// Two gates (Cameron, 2026-10-09): the chat's canvas belongs to the agentic chat's flag; onboarding adds its own.

/**
 * Everything the chat does with the canvas: declaring it, opening it from a turn, a resume's routes, `/post/*` and a
 * canvas opened by URL. It sits on the docked chat's flag and opens at any width, for admins too.
 */
export const canvasEnabled = (flags: Pick<TakeoverFlags, "chatEnabled">) => flags.chatEnabled;

/** Onboarding only: the first-post redirect, the get-started landing and its offers. */
export const onboardingOn = (flags: TakeoverFlags) => canvasEnabled(flags) && flags.onboardingEnabled;

/** Whether the chat posts on the canvas here: its routes are a location manager's, so an onsite manager posts in chat. */
export const canvasPostingOpen = (gate: { enabled: boolean; isOnsiteManager: boolean }) =>
  gate.enabled && !gate.isOnsiteManager;

/**
 * A `?position=` from the URL, as the post form's positions list names it, matched without case. Anything else is
 * dropped, so no free text from a link reaches the agent: the canvas starts at the role question instead.
 */
export const knownPosition = (position: string | undefined, positions: ReadonlyArray<{ name: string }>) => {
  const wanted = position?.trim().toLowerCase();
  return wanted ? positions.find((known) => known.name.toLowerCase() === wanted)?.name : undefined;
};

/** Onboarding offered at this location: an onsite manager would only be redirected off the takeover's route. */
export const onboardingOffered = (gate: {
  enabled: boolean;
  locationId: number | undefined;
  skipped: ReadonlyArray<number>;
  isOnsiteManager: boolean;
}) => gate.enabled && gate.locationId !== undefined && !gate.isOnsiteManager && !gate.skipped.includes(gate.locationId);

/**
 * Where an explicit job request (`/post/job`) lands. Unlike the onboarding landing it ignores the first
 * post and Skip to Home: the manager asked for it.
 */
export const jobEntryDestination = (gate: { enabled: boolean; jobsAccess: boolean }) => {
  if (!gate.jobsAccess) return "home";
  return gate.enabled ? "takeover" : "hire-form";
};
